import { types } from "mobx-state-tree";
/**
 * This is the serializable version of IDocumentMetadata. It is almost the same. The one
 * important difference is the `properties` property.
 * In this MST model that property is an `observable.map<string, string>`.
 * In the IDocumentMetadata it is a Record<string,string>.
 */
export const DocumentMetadataModel = types.model("DocumentMetadata", {
    uid: types.string,
    type: types.string,
    key: types.identifier,
    createdAt: types.maybe(types.number),
    context_id: types.maybeNull(types.string),
    /**
     * The creating user's primary network (or null). It is not recommended to use this
     * property. See docs/document-metadata/metadata-fields.md for more information.
     */
    network: types.maybeNull(types.string),
    /**
     * If the document is a group document this is the id the group that owns the document.
     * If the document is not a group document this should be undefined because the group
     * of the user of the document might change and we don't want to store stale group ids,
     * or try to keep them all updated.
     */
    groupId: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    originDoc: types.maybeNull(types.string),
    properties: types.map(types.union(types.string, types.number)),
    tools: types.array(types.string),
    strategies: types.array(types.string),
    investigation: types.maybeNull(types.string),
    problem: types.maybeNull(types.string),
    unit: types.maybeNull(types.string),
    visibility: types.maybeNull(types.string),
    lastHistoryEntry: types.maybeNull(types.model({
        id: types.string,
        index: types.number,
    })),
})
    .views((self) => ({
    get propertiesAsStringRecord() {
        const record = {};
        self.properties.forEach((value, key) => {
            record[key] = String(value);
        });
        return record;
    },
    /**
     * In the database there can be properties with numeric values.
     * For consistency with the other document types we always return these as Strings.
     * @param key
     */
    getProperty(key) {
        const value = self.properties.get(key);
        if (value === undefined) {
            return undefined;
        }
        return String(value);
    },
}));
export const MetadataDocMapModel = types.map(DocumentMetadataModel);
//
// Compile-time type checks
// =========================
// These make sure that IDocumentMetadataModel is compatible with IDocumentMetadata.
// The `properties` field is omitted because the model uses an observable map while
// the interface uses Record<string, string>.
/**
 * This is function is not called anywhere. Its existence forces the Typescript compiler
 * to check the basic compatibility between IDocumentMetadataModel and IDocumentMetadata.
 * Since many of the properties are optional, this check mainly catches type mismatches.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
function _checkDocumentMetadataModelMatchesInterface(_model) {
    return _model;
}
/**
 * This is function is not called anywhere. Its existence forces the Typescript compiler
 * to check that all of the properties match between IDocumentMetadataModel and IDocumentMetadata.
 * It does this by making all properties required.
 * The createdAt property is excluded because in the MST model it is maybe, which results in
 * a typescript type of `number | undefined`, which doesn't get stripped out by the Required<T>
 * utility type.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
function _checkDocumentMetadataModelMatchesFullInterface(_model) {
    return _model;
}
/**
 * This is function is not called anywhere. Its existence forces the Typescript compiler
 * to check that the snapshot type of DocumentMetadataModel matches IDocumentMetadata.
 * The DocumentMetadataModel is created from the data in Firestore which is supposed
 * to match the IDocumentMetadata interface. So this checks to make sure at least
 * the types support that creation. The types are modified to have all required
 * properties so it will pick up missing properties.
 */
// eslint-disable-next-line unused-imports/no-unused-vars
function _checkInterfaceMatchesDocumentMetadataModelSnapshotIn(_model) {
    return _model;
}
//
// End of compile-time type checks
//
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZG9jdW1lbnQtbWV0YWRhdGEtbW9kZWwuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbW9kZWxzL2RvY3VtZW50L2RvY3VtZW50LW1ldGFkYXRhLW1vZGVsLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxLQUFLLEVBQXdCLE1BQU0saUJBQWlCLENBQUM7QUFHOUQ7Ozs7O0dBS0c7QUFDSCxNQUFNLENBQUMsTUFBTSxxQkFBcUIsR0FBRyxLQUFLLENBQUMsS0FBSyxDQUFDLGtCQUFrQixFQUFFO0lBQ25FLEdBQUcsRUFBRSxLQUFLLENBQUMsTUFBTTtJQUNqQixJQUFJLEVBQUUsS0FBSyxDQUFDLE1BQU07SUFDbEIsR0FBRyxFQUFFLEtBQUssQ0FBQyxVQUFVO0lBQ3JCLFNBQVMsRUFBRSxLQUFLLENBQUMsS0FBSyxDQUFDLEtBQUssQ0FBQyxNQUFNLENBQUM7SUFDcEMsVUFBVSxFQUFFLEtBQUssQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztJQUN6Qzs7O09BR0c7SUFDSCxPQUFPLEVBQUUsS0FBSyxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDO0lBQ3RDOzs7OztPQUtHO0lBQ0gsT0FBTyxFQUFFLEtBQUssQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztJQUN0QyxLQUFLLEVBQUUsS0FBSyxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDO0lBQ3BDLFNBQVMsRUFBRSxLQUFLLENBQUMsU0FBUyxDQUFDLEtBQUssQ0FBQyxNQUFNLENBQUM7SUFDeEMsVUFBVSxFQUFFLEtBQUssQ0FBQyxHQUFHLENBQUMsS0FBSyxDQUFDLEtBQUssQ0FBQyxLQUFLLENBQUMsTUFBTSxFQUFFLEtBQUssQ0FBQyxNQUFNLENBQUMsQ0FBQztJQUM5RCxLQUFLLEVBQUUsS0FBSyxDQUFDLEtBQUssQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDO0lBQ2hDLFVBQVUsRUFBRSxLQUFLLENBQUMsS0FBSyxDQUFDLEtBQUssQ0FBQyxNQUFNLENBQUM7SUFDckMsYUFBYSxFQUFFLEtBQUssQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztJQUM1QyxPQUFPLEVBQUUsS0FBSyxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsTUFBTSxDQUFDO0lBQ3RDLElBQUksRUFBRSxLQUFLLENBQUMsU0FBUyxDQUFDLEtBQUssQ0FBQyxNQUFNLENBQUM7SUFDbkMsVUFBVSxFQUFFLEtBQUssQ0FBQyxTQUFTLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQztJQUN6QyxnQkFBZ0IsRUFBRSxLQUFLLENBQUMsU0FBUyxDQUFDLEtBQUssQ0FBQyxLQUFLLENBQUM7UUFDNUMsRUFBRSxFQUFFLEtBQUssQ0FBQyxNQUFNO1FBQ2hCLEtBQUssRUFBRSxLQUFLLENBQUMsTUFBTTtLQUNwQixDQUFDLENBQUM7Q0FDSixDQUFDO0tBQ0QsS0FBSyxDQUFDLENBQUMsSUFBSSxFQUFFLEVBQUUsQ0FBQyxDQUFDO0lBQ2hCLElBQUksd0JBQXdCO1FBQzFCLE1BQU0sTUFBTSxHQUEyQixFQUFFLENBQUM7UUFDMUMsSUFBSSxDQUFDLFVBQVUsQ0FBQyxPQUFPLENBQUMsQ0FBQyxLQUFLLEVBQUUsR0FBRyxFQUFFLEVBQUU7WUFDckMsTUFBTSxDQUFDLEdBQUcsQ0FBQyxHQUFHLE1BQU0sQ0FBQyxLQUFLLENBQUMsQ0FBQztRQUM5QixDQUFDLENBQUMsQ0FBQztRQUNILE9BQU8sTUFBTSxDQUFDO0lBQ2hCLENBQUM7SUFDRDs7OztPQUlHO0lBQ0gsV0FBVyxDQUFDLEdBQVc7UUFDckIsTUFBTSxLQUFLLEdBQUcsSUFBSSxDQUFDLFVBQVUsQ0FBQyxHQUFHLENBQUMsR0FBRyxDQUFDLENBQUM7UUFDdkMsSUFBSSxLQUFLLEtBQUssU0FBUyxFQUFFLENBQUM7WUFDeEIsT0FBTyxTQUFTLENBQUM7UUFDbkIsQ0FBQztRQUNELE9BQU8sTUFBTSxDQUFDLEtBQUssQ0FBQyxDQUFDO0lBQ3ZCLENBQUM7Q0FDRixDQUFDLENBQUMsQ0FBQztBQUlKLE1BQU0sQ0FBQyxNQUFNLG1CQUFtQixHQUFHLEtBQUssQ0FBQyxHQUFHLENBQUMscUJBQXFCLENBQUMsQ0FBQztBQUVwRSxFQUFFO0FBQ0YsMkJBQTJCO0FBQzNCLDRCQUE0QjtBQUM1QixvRkFBb0Y7QUFDcEYsbUZBQW1GO0FBQ25GLDZDQUE2QztBQUU3Qzs7OztHQUlHO0FBQ0gseURBQXlEO0FBQ3pELFNBQVMsMkNBQTJDLENBQ2xELE1BQWtEO0lBRWxELE9BQU8sTUFBTSxDQUFDO0FBQ2hCLENBQUM7QUFFRDs7Ozs7OztHQU9HO0FBQ0gseURBQXlEO0FBQ3pELFNBQVMsK0NBQStDLENBQ3RELE1BQTBFO0lBRTFFLE9BQU8sTUFBTSxDQUFDO0FBQ2hCLENBQUM7QUFHRDs7Ozs7OztHQU9HO0FBQ0gseURBQXlEO0FBQ3pELFNBQVMscURBQXFELENBQzVELE1BQW1DO0lBRW5DLE9BQU8sTUFBTSxDQUFDO0FBQ2hCLENBQUM7QUFFRCxFQUFFO0FBQ0Ysa0NBQWtDO0FBQ2xDLEVBQUUifQ==